import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Container,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Slide,
    AppBar,
    Toolbar,
    Link,
    Chip,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosNewIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useAuth } from '../auth/AuthContext';
import { Stack } from '@mui/system';
import { useParams, useNavigate } from 'react-router-dom';

// Fix for default Leaflet icons
L.Icon.Default.prototype._get = 'https://bluejims.com:5005/misbolos/image/d36a995e-141d-44dd-9d62-a58f4c1737e8';

L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Define a transition for the Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const GroupsMapScreen = () => {
    const { currentUser, dbUser, isLoading: authLoading } = useAuth();
    const { groupName } = useParams();
    const navigate = useNavigate();

    const [allGroups, setAllGroups] = useState([]);
    const [hasDataProcessed, setHasDataProcessed] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        if (openDialog) {
            setCurrentImageIndex(0);
        }
    }, [openDialog, selectedGroup]);

    useEffect(() => {
        const fetchGroups = async () => {
            if (!authLoading && dbUser) {
                try {
                    let fetchedGroups = [];
                    if (dbUser.getPublicGroups) {
                        console.warn('Fetching getPublicGroups with hardcoded values for the filter')
                        fetchedGroups = await dbUser.getPublicGroups({ pageNum: 1, searchTerm: null, limit: 1000 });
                    } else if (dbUser.publicGroups) {
                        if (Array.isArray(dbUser.publicGroups)) {
                            fetchedGroups = Object.values(dbUser.publicGroups);
                        }
                    }
                    setAllGroups(fetchedGroups);

                    if (groupName) {
                        const decodedGroupName = decodeURIComponent(groupName).replace(/-/g, ' ');
                        const groupToOpen = fetchedGroups.find(
                            (group) => group.name.toLowerCase() === decodedGroupName.toLowerCase()
                        );
                        if (groupToOpen) {
                            setSelectedGroup(groupToOpen);
                            setOpenDialog(true);
                        } else {
                            console.warn(`Grupo "${decodedGroupName}" no encontrado.`);
                            navigate('/misbolos/groups');
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch public groups:', error);
                    setAllGroups([]);
                } finally {
                    setHasDataProcessed(true);
                }
            } else if (!authLoading && !dbUser) {
                setAllGroups([]);
                setHasDataProcessed(true);
            }
        };

        fetchGroups();
    }, [authLoading, dbUser, groupName, navigate]);

    const handleMarkerClick = (group) => {
        setSelectedGroup(group);
    };

    const handleViewDetails = () => {
        if (selectedGroup) {
            const encodedGroupName = encodeURIComponent(selectedGroup.name).replace(/ /g, '-');
            navigate(`/misbolos/groups/${encodedGroupName}`);
            setOpenDialog(true);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedGroup(null);
        navigate('/misbolos/groups/map'); // Navigate back to the map view
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === selectedGroup.images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const handlePreviousImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? selectedGroup.images.length - 1 : prevIndex - 1
        );
    };

    // Filter groups to only include those with valid coordinates
    const groupsWithLocation = useMemo(() => {
        return allGroups.filter(group =>
            group.location &&
            typeof group.location.latitude === 'number' &&
            typeof group.location.longitude === 'number'
        );
    }, [allGroups]);

    if (authLoading || !hasDataProcessed) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ marginLeft: 2 }}>Cargando grupos...</Typography>
            </Box>
        );
    }

    // Set default center to A Coruña, Spain if no groups or no location data
    const defaultCenter = [43.3623, -8.3946];
    const mapCenter = groupsWithLocation.length > 0
        ? [groupsWithLocation[0].location.latitude, groupsWithLocation[0].location.longitude]
        : defaultCenter;

    return (
        <Box sx={{ minHeight: '100vh', background: '#f0f0f0', padding: 2 }}>
            <Container maxWidth="xl" sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h3" fontWeight="bold" align="center" gutterBottom sx={{ mt: 2, mb: 3 }}>
                    {/* Mapa de Bandas Cercanas */}
                    Vista de Mapa
                </Typography>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    Grupos cerca de ti
                </Typography>
                {groupsWithLocation.length === 0 ? (
                    <Box sx={{ textAlign: 'center', mt: 8 }}>
                        <Typography variant="h5" color="text.secondary">
                            No se encontraron grupos con información de ubicación.
                        </Typography>
                        {!dbUser && (
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                                Por favor, inicia sesión para ver grupos públicos o asegúrate de que tu cuenta tenga grupos con ubicaciones válidas.
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <MapContainer
                        center={mapCenter}
                        zoom={13}
                        scrollWheelZoom={false}
                        style={{ height: '70vh', width: '100%', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {groupsWithLocation.map((group) => (
                            <Marker
                                key={group._id || group.id}
                                position={[group.location.latitude, group.location.longitude]}
                                eventHandlers={{
                                    click: () => handleMarkerClick(group),
                                }}
                            >
                                <Popup>
                                    <Box>
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                                            {group.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {group.description}
                                        </Typography>
                                        <button
                                            onClick={handleViewDetails}
                                            style={{
                                                padding: '8px 12px',
                                                backgroundColor: '#3f51b5',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                marginTop: '8px',
                                            }}
                                        >
                                            Ver Detalles
                                        </button>
                                    </Box>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                )}
            </Container>

            {/* Group Detail Dialog (Reused from your original component) */}
            <Dialog
                fullScreen
                open={openDialog}
                onClose={handleCloseDialog}
                TransitionComponent={Transition}
            >
                <AppBar sx={{ position: 'relative', background: 'linear-gradient(to right,rgb(31, 31, 31),rgb(20, 20, 20))' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleCloseDialog}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Detalles del Grupo
                        </Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent dividers sx={{ p: 0, '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                    {selectedGroup && (
                        <Box sx={{ p: 3 }}>
                            {/* Image carousel with navigation arrows */}
                            {selectedGroup.images && selectedGroup.images.length > 0 && (
                                <Box sx={{ position: 'relative', mb: 3 }}>
                                    <img
                                        src={`https://bluejims.com:5005/misbolos/image/${selectedGroup.images[currentImageIndex]}`}
                                        alt={selectedGroup.name}
                                        style={{
                                            maxHeight: 400,
                                            width: '100%',
                                            objectFit: 'cover',
                                            borderRadius: 8,
                                        }}
                                    />
                                    {selectedGroup.images.length > 1 && (
                                        <>
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: 8,
                                                    transform: 'translateY(-50%)',
                                                    color: 'white',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                    },
                                                }}
                                                onClick={handlePreviousImage}
                                                aria-label="previous image"
                                            >
                                                <ArrowBackIosNewIcon />
                                            </IconButton>
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    right: 8,
                                                    transform: 'translateY(-50%)',
                                                    color: 'white',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                    },
                                                }}
                                                onClick={handleNextImage}
                                                aria-label="next image"
                                            >
                                                <ArrowForwardIosNewIcon />
                                            </IconButton>
                                        </>
                                    )}
                                    {/* Image counter */}
                                    {selectedGroup.images.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                right: 8,
                                                color: 'white',
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                borderRadius: 1,
                                                padding: '2px 6px',
                                            }}
                                        >
                                            {currentImageIndex + 1} / {selectedGroup.images.length}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#3f51b5' }}>
                                {selectedGroup.name}
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    🎂 <Box component="span" sx={{ ml: 1, fontWeight: 'medium' }}>Fundado: {new Date(selectedGroup.birthdate).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</Box>
                                </Typography>
                                {selectedGroup.location && (
                                    <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        📍 <Box component="span" sx={{ ml: 1, fontWeight: 'medium' }}>Lat: {selectedGroup.location.latitude.toFixed(2)}, Lon: {selectedGroup.location.longitude.toFixed(2)}</Box>
                                    </Typography>
                                )}
                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: '7px' }}>
                                    {selectedGroup.members.map((member, index) => (
                                        <Chip
                                            key={index}
                                            label={member.email}
                                            clickable
                                            color="primary"
                                            onClick={() => navigate(`/misbolos/users/${member.email}`)}
                                        />
                                    ))}
                                </Box>
                                <Chip
                                    label={`Activo: ${selectedGroup.active ? 'Sí' : 'No'}`}
                                    color={selectedGroup.active ? "success" : "default"}
                                    sx={{ fontSize: '1rem', padding: '5px 10px', height: 'auto', fontWeight: 'bold' }}
                                />
                            </Box>

                            <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                Descripción:
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {selectedGroup.description}
                            </Typography>

                            {selectedGroup.services && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                        Servicios:
                                    </Typography>
                                    <Typography variant="body1" paragraph>
                                        {selectedGroup.services}
                                    </Typography>
                                </>
                            )}

                            {selectedGroup.links && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                        Enlaces:
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                                        {selectedGroup.links.website && (
                                            <Link href={selectedGroup.links.website} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                <LanguageIcon sx={{ mr: 0.5 }} /> Web
                                            </Link>
                                        )}
                                        {selectedGroup.links.facebook && (
                                            <Link href={selectedGroup.links.facebook} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                <FacebookIcon sx={{ mr: 0.5 }} /> Facebook
                                            </Link>
                                        )}
                                        {selectedGroup.links.instagram && (
                                            <Link href={selectedGroup.links.instagram} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                <InstagramIcon sx={{ mr: 0.5 }} /> Instagram
                                            </Link>
                                        )}
                                        {selectedGroup.links.youtube && (
                                            <Link href={selectedGroup.links.youtube} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                <YouTubeIcon sx={{ mr: 0.5 }} /> YouTube
                                            </Link>
                                        )}
                                        {selectedGroup.links.bandcamp && (
                                            <Link href={selectedGroup.links.bandcamp} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                Bandcamp
                                            </Link>
                                        )}
                                    </Stack>
                                </>
                            )}

                            {selectedGroup.activities && selectedGroup.activities.length > 0 && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                        Actividades Recientes:
                                    </Typography>
                                    <Stack spacing={1}>
                                        {selectedGroup.activities.map((activity, index) => (
                                            <Chip
                                                key={activity._id || index}
                                                label={`${activity.title} (${new Date(activity.date).toLocaleDateString('es-ES')})`}
                                                color="info"
                                                onClick={() => navigate(`/misbolos/activities/${activity.title}`)}
                                                sx={{ backgroundColor: 'rgb(36, 36, 36)', color: 'white', fontWeight: 'bold' }}
                                            />
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default GroupsMapScreen;